import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { RiCloseCircleLine } from 'react-icons/ri';
import * as Yup from 'yup';

import Loader from '../Loader';
import { accountStatusColor } from '../utils/accountStatusColor';

import useUpdateUser from '@/hooks/users/useUpdateUser';
import { IUser } from '@/interfaces/entities/user';
import { AccountStatus } from '@/interfaces/entities/user.account-status';
import { notificationService } from '@/services/notification.service';

interface BlockNotificationValues {
	message: string;
	action: string;
	selectedDateTime: Date;
	selectedUsers: number[];
}

export const Block = ({
	user,
	handleCloseBlock,
}: {
	user: IUser;
	handleCloseBlock: () => void;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const updateApplicationStatus = useUpdateUser();
	const changeStatus = async (status: AccountStatus, message: string) => {
		if (!user.id) return;

		updateApplicationStatus.mutate({ userId: user.id, status, message });
	};

	const handleBlock = async (
		values: BlockNotificationValues,
		{ setSubmitting }: FormikHelpers<BlockNotificationValues>,
	) => {
		if (!user.id) return;
		setIsLoading(true);
		setSubmitting(true);
		const { action, message } = values;

		try {
			await changeStatus(
				action === 'accept' ? AccountStatus.Accepted : AccountStatus.Rejected,
				message,
			);

			notificationService.success(
				`User ${action === 'accept' ? 'accepted' : 'rejected'}`,
			);
			handleCloseBlock();
		} catch (error) {
			console.error(error);
			notificationService.error('Error updating user');
		}

		setSubmitting(false);
		setIsLoading(false);
	};

	const BlockSchema = Yup.object().shape({
		message: Yup.string().required('Message is required'),
	});

	return (
		<div className="bg-white rounded-lg shadow-lg py-1 px-1">
			<div className="flex justify-end">
				<button onClick={handleCloseBlock} data-test="manage-access-close">
					<RiCloseCircleLine size={20} />
				</button>
			</div>
			<div>
				<h2 className="text-2xl font-bold mb-4" data-test="manage-access-title">
					Manage Access
				</h2>
				<div data-test="user-info">
					<b>User:</b> {user.firstName} {user.lastName} ({user.username})
				</div>
				<div>
					<b>Current Status:</b>{' '}
					<span className={accountStatusColor(user)}>
						{user.applicationRequest}
					</span>
				</div>
			</div>

			<Formik
				initialValues={{
					message: '',
					action: '',
					selectedDateTime: new Date(),
					selectedUsers: [user.id as number],
				}}
				validationSchema={BlockSchema}
				onSubmit={handleBlock}
			>
				{({ handleSubmit, setFieldValue, isSubmitting, errors }) => (
					<Form>
						<div>
							<div className="mt-2">Message</div>
							<Field
								className="w-full bg-white border border-gray-300 rounded-md p-2"
								placeholder="Message"
								name="message"
								as="textarea"
								rows={5}
								style={{
									resize: 'none',
									overflowY: 'auto',
								}}
								data-test="manage-access-message"
							/>
							{errors.message && (
								<p className="text-red-500 text-sm">{errors.message}</p>
							)}
						</div>
						{isSubmitting ? (
							<Loader />
						) : (
							<>
								{user.applicationRequest === AccountStatus.Pending && (
									<div className="flex justify-between mt-4 mb-2">
										<button
											type="button"
											className="bg-green-500 hover:bg-green-400 text-white items-center justify-center px-3 py-1 rounded-md mx-4 ml-2 w-[40%]"
											onClick={() => {
												setFieldValue('action', 'accept');
												handleSubmit();
											}}
											disabled={isSubmitting}
											data-test="manage-access-accept-button"
										>
											ACCEPT
										</button>
										<button
											type="button"
											className="bg-red-500 hover:bg-red-400 text-white items-center justify-center px-3 py-1 rounded-md mx-4 ml-2 w-[40%]"
											onClick={() => {
												setFieldValue('action', 'reject');
												handleSubmit();
											}}
											disabled={isSubmitting}
											data-test="manage-access-reject-button"
										>
											REJECT
										</button>
									</div>
								)}
								{user.applicationRequest !== AccountStatus.Pending && (
									<div className="flex justify-center mt-4">
										{user.applicationRequest === AccountStatus.Accepted ? (
											<button
												type="button"
												className="bg-red-500 hover:bg-red-400 text-white items-center justify-center px-3 py-1 rounded-md mb-2 ml-2 w-[40%] disabled:opacity-50"
												onClick={() => {
													setFieldValue('action', 'reject');
													handleSubmit();
												}}
												disabled={isSubmitting || isLoading}
												data-test="manage-access-block-button"
											>
												BLOCK
											</button>
										) : (
											<button
												type="button"
												className="bg-green-500 hover:bg-green-400 text-white items-center justify-center px-3 py-1 rounded-md mb-2 ml-2 w-[40%] disabled:opacity-50"
												onClick={() => {
													setFieldValue('action', 'accept');
													handleSubmit();
												}}
												disabled={isSubmitting || isLoading}
												data-test="manage-access-unblock-button"
											>
												UNBLOCK
											</button>
										)}
									</div>
								)}
							</>
						)}
					</Form>
				)}
			</Formik>
		</div>
	);
};
